
/**
 * TiledWindowContainer - Container component that renders all open tiled windows
 * 
 * This component should be placed at a high level in the component tree and
 * uses the TiledWindowContext to manage and render windows.
 * 
 * Windows are rendered inside a positioned container, allowing them to be
 * constrained to the parent's bounds (e.g., workflow graph area).
 */

import { useCallback, useRef, type ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { TiledWindow } from './TiledWindow';
import { useTiledWindows, type OpenWindowRequest } from './TiledWindowContext';
import type { 
    TiledWindow as TiledWindowType, 
    WorkflowWindow, 
    AgentWindow,
    ToolWindow,
    PromptWindow,
    ModelWindow,
} from '@/types/tiled-window';

// Lazy-loaded entity viewers
import { WorkflowWindowContent } from './viewers/WorkflowWindowContent';
import { AgentWindowContent } from './viewers/AgentWindowContent';
import { ToolWindowContent } from './viewers/ToolWindowContent';
import { PromptWindowContent } from './viewers/PromptWindowContent';
import { ModelWindowContent } from './viewers/ModelWindowContent';
import { GenericWindowContent } from './viewers/GenericWindowContent';
import { DecisionWindowContent } from './viewers/DecisionWindowContent';

interface TiledWindowContainerProps {
    /** Children to render (the main app content) */
    children?: ReactNode;
}

/**
 * Container that renders all tiled windows from context.
 * Windows are rendered inside an absolutely positioned layer that
 * overlays the children, constrained to the container bounds.
 */
export function TiledWindowContainer({ children }: TiledWindowContainerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const {
        windows,
        focusedWindowId,
        focusWindow,
        closeWindow,
        updateGeometry,
        updateState,
        openWindow,
    } = useTiledWindows();

    /**
     * Handle opening external (navigate to full page)
     * 
     * Uses the actual database ID from the loaded entity data, not entityId which
     * might be a key/name rather than a UUID.
     */
    const handleOpenExternal = useCallback((window: TiledWindowType) => {
        // Extract the actual database ID from the loaded entity data
        // The entityId might be a key/name (e.g., "workflow.casefile.ingest") but
        // the page routes expect the actual UUID from the database record
        const getActualId = (window: TiledWindowType): string | number | null => {
            if (!window.data) return null;
            
            switch (window.entityType) {
                case 'workflow':
                    return (window as WorkflowWindow).data.workflow?.id;
                case 'agent':
                    return (window as AgentWindow).data.agent?.slug || (window as AgentWindow).data.agent?.name;
                case 'tool':
                    return (window as ToolWindow).data.tool?.id;
                case 'prompt':
                    return (window as PromptWindow).data.prompt?.id;
                case 'model':
                    return (window as ModelWindow).data.model?.id;
                default:
                    return null;
            }
        };

        const actualId = getActualId(window);
        if (!actualId) {
            console.warn('[TiledWindow] Could not determine actual entity ID for external navigation', {
                entityType: window.entityType,
                entityId: window.entityId,
                hasData: !!window.data,
            });
            return;
        }

        const routes: Record<string, (id: string | number) => string> = {
            workflow: (id) => `/admin/workflows/${id}`,
            agent: (id) => `/admin/agents/${id}`,
            tool: (id) => `/admin/tools/${id}`,
            prompt: (id) => `/admin/prompts/${id}`,
            model: (id) => `/admin/models/${id}`,
            skill: (id) => `/admin/skills/${id}`,
            role: (id) => `/admin/roles/${id}`,
            policy: (id) => `/admin/policies/${id}`,
            schema: (id) => `/admin/schemas/${id}`,
        };

        const routeFn = routes[window.entityType];
        if (routeFn) {
            // Open in new tab
            const url = routeFn(actualId);
            globalThis.open(url, '_blank');
        }
    }, []);

    /**
     * Render content based on window type
     */
    const renderWindowContent = useCallback((windowData: TiledWindowType): ReactNode => {
        // Handle drill-down navigation
        const handleOpenEntity = (request: OpenWindowRequest) => {
            openWindow({
                ...request,
                parentWindowId: windowData.id,
            });
        };

        switch (windowData.entityType) {
            case 'workflow':
                return (
                    <WorkflowWindowContent 
                        window={windowData as WorkflowWindow} 
                        onOpenEntity={handleOpenEntity}
                    />
                );
            case 'agent':
                return (
                    <AgentWindowContent 
                        window={windowData as AgentWindow} 
                    />
                );
            case 'tool':
                return (
                    <ToolWindowContent 
                        window={windowData as ToolWindow}
                    />
                );
            case 'prompt':
                return (
                    <PromptWindowContent 
                        window={windowData as PromptWindow}
                    />
                );
            case 'model':
                return (
                    <ModelWindowContent 
                        window={windowData as ModelWindow}
                    />
                );
            case 'decision':
                return (
                    <DecisionWindowContent
                        window={windowData as import('@/types/tiled-window').DecisionWindow}
                    />
                );
            default:
                return (
                    <GenericWindowContent window={windowData} />
                );
        }
    }, [openWindow]);

    const hasWindows = windows.length > 0;
    const hasMinimizedWindows = windows.some(w => w.state === 'minimized');

    return (
        <div ref={containerRef} className="relative h-full w-full flex-1">
            {/* Main app content */}
            {children}

            {/* Window layer - positioned absolutely within this container */}
            {/* Always render so AnimatePresence can play exit animations */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <AnimatePresence>
                    {windows.map((window) => (
                        <TiledWindow
                            key={window.id}
                            window={window}
                            isFocused={focusedWindowId === window.id}
                            onFocus={() => focusWindow(window.id)}
                            onClose={() => closeWindow(window.id)}
                            onGeometryChange={(geometry) => updateGeometry(window.id, geometry)}
                            onStateChange={(state) => updateState(window.id, state)}
                            renderContent={renderWindowContent}
                            onOpenExternal={handleOpenExternal}
                            containerRef={containerRef}
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* Minimized windows taskbar */}
            {hasMinimizedWindows && (
                <MinimizedWindowsTaskbar />
            )}
        </div>
    );
}

/**
 * Taskbar showing minimized windows
 */
function MinimizedWindowsTaskbar() {
    const { windows, focusWindow, updateState, closeWindow } = useTiledWindows();
    
    const minimizedWindows = windows.filter(w => w.state === 'minimized');
    
    if (minimizedWindows.length === 0) return null;

    return (
        <div className="fixed bottom-4 left-1/2 z-[2000] flex -translate-x-1/2 items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
            {minimizedWindows.map((window) => (
                <button
                    key={window.id}
                    className="flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    onClick={() => {
                        updateState(window.id, 'normal');
                        focusWindow(window.id);
                    }}
                    title={`Restore ${window.title}`}
                >
                    <span className="max-w-[120px] truncate">{window.title}</span>
                    <button
                        className="ml-1 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                        onClick={(e) => {
                            e.stopPropagation();
                            closeWindow(window.id);
                        }}
                    >
                        ×
                    </button>
                </button>
            ))}
        </div>
    );
}

